import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Loader2, Cylinder, Building2, FileSearch, FileText, ClipboardList, FileCheck2 } from "lucide-react";
import CommercialStatusBadge from "@/components/CommercialStatusBadge";

const RESOURCE_META = {
  cylinders: { label: "Cylinders", icon: Cylinder, path: (r) => `/cylinders/${r.id}`, primary: "cylinder_number", statusField: "cylinder_status" },
  customers: { label: "Customers", icon: Building2, path: () => "#", primary: "name" },
  inquiries: { label: "Inquiries", icon: FileSearch, path: (r) => `/inquiries/${r.id}`, primary: "inquiry_number", statusField: "status" },
  quotations: { label: "Quotations", icon: FileText, path: (r) => `/quotations/${r.id}`, primary: "quotation_number", statusField: "status" },
  purchase_orders: { label: "Purchase Orders", icon: ClipboardList, path: (r) => `/purchase-orders/${r.id}`, primary: "po_number", statusField: "status" },
  order_acceptances: { label: "Order Acceptances", icon: FileCheck2, path: (r) => `/order-acceptances/${r.id}`, primary: "oa_number", statusField: "status" },
};

export default function GlobalSearch() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setResults(null); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`)
        .then((r) => setResults(r.data.results))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  function go(path) {
    setOpen(false); setQ(""); setResults(null);
    nav(path);
  }

  const totalHits = results
    ? Object.values(results).reduce((s, arr) => s + arr.length, 0)
    : 0;

  return (
    <div className="relative">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
        <Input
          data-testid="global-search"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder="Search cylinders, customers, PO, OA..."
          className="pl-8 h-9 w-72 text-sm"
        />
      </div>
      {open && q.length >= 2 && (
        <div className="absolute right-0 top-11 w-96 max-h-[70vh] overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg z-50">
          {loading && (
            <div className="p-4 text-center text-xs text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Searching...
            </div>
          )}
          {!loading && results && totalHits === 0 && (
            <div className="p-4 text-center text-xs text-slate-500">No results for &quot;{q}&quot;</div>
          )}
          {!loading && results && Object.entries(results).map(([key, items]) => {
            if (!items.length) return null;
            const meta = RESOURCE_META[key];
            return (
              <div key={key} className="border-b border-slate-100 last:border-0">
                <div className="px-3 py-1.5 bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                  <meta.icon className="w-3 h-3" />
                  {meta.label} ({items.length})
                </div>
                {items.map((item) => (
                  <button
                    key={item.id}
                    data-testid={`search-result-${item[meta.primary]}`}
                    onMouseDown={(e) => { e.preventDefault(); go(meta.path(item)); }}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50/50 flex items-center justify-between border-t border-slate-100"
                  >
                    <div className="min-w-0">
                      <div className="font-mono text-xs font-semibold text-blue-900 truncate">{item[meta.primary]}</div>
                      {item.customer_name && (
                        <div className="text-[11px] text-slate-500 truncate">{item.customer_name}</div>
                      )}
                    </div>
                    {meta.statusField && item[meta.statusField] && (
                      <CommercialStatusBadge status={item[meta.statusField]} />
                    )}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
